<template>
  <div class="inner">
    <div class="page-header">
      <h2 data-test="market-summary-title">{{ coverageType }} {{ layerName }} Layer Marketing Update</h2>
      <div class="table-container">
        <table id="market-summary" class="apptable">
          <thead>
          <tr>
            <td class="nosort">Carrier</td>
            <td class="nosort number">Limit</td>
            <td class="nosort number" v-if="showRetention">Retention</td>
            <td class="nosort number" v-if="showAttachment">Attachment</td>
            <td class="nosort number">Premium</td>
            <td class="nosort number">PPM</td>
            <td class="nosort text-center">Admitted</td>
            <td class="nosort number">Comm.</td>
            <template v-if="quoteItemTypes">
              <td v-for="t in quoteItemTypes" :key="t.quoteItemTypeId" :class="`nosort ${itemClasses(t.optionType)}`">{{ t.name }}</td>
            </template>
          </tr>
          </thead>
          <tbody>
          <tr :class="{ declined: carrier.declined }" v-for="carrier in carriers" :key="carrier.key">
            <td><b>{{ carrier.name }}</b></td>
            <td v-if="carrier.declined" colspan="5">Declined due to {{ carrier.declinedReason }}.</td> <!-- todo: update colspan -->
            <template v-if="!carrier.declined">
              <td class="number" :data-raw="carrier.limit">{{ filters.compactMoney(carrier.limit) ?? '--' }}</td>
              <td class="number" v-if="showRetention" :data-raw="carrier.retention">{{ filters.compactMoney(carrier.retention) ?? '--' }}</td>
              <td class="number" v-if="showAttachment" :data-raw="carrier.attachment">{{ filters.compactMoney(carrier.attachment) ?? '--' }}</td>
              <td class="number" :data-raw="carrier.premium">{{ filters.compactMoney(carrier.premium) ?? '--' }}</td>
              <td class="number" :data-raw="carrier.ppm">{{ filters.compactMoney(carrier.ppm) ?? '--' }}</td>
              <td class="text-center">{{ carrier.admitted ?? '--' }}</td>
              <td class="number" :data-raw="carrier.commissionPercent">{{ carrier.commPctDisplay ?? '--' }}</td>
              <template v-if="quoteItemTypes">
                <td v-for="t in quoteItemTypes" :key="t.quoteItemTypeId" :class="`${itemClasses(t.optionType)}`">
                  {{ itemValueForType(t, carrier.quoteItems) }}
                </td>
              </template>
            </template>
          </tr>
          </tbody>
        </table>
      </div>
      <template v-if="numHiddenFields > 0">
        <div class="mt-2 disclaimer">
          * Some fields are only available in the downloadable CSV.
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { Quote, QuoteItem, QuoteItemType } from '@/types/Quote';
import { addCommas } from '@/utilities/Number';
import { filters } from '@/utilities/Filters';

function isTextField(quoteItemOptionType: string): boolean {
  return quoteItemOptionType === 'text' || quoteItemOptionType === 'string';
}

export default {
  name: 'MarketSummary',
  props: {
    coverageType: String,
    layerName: String,
    layerType: String,
    quotes: Array as () => Quote[],
    quoteItemTypes: Array as () => QuoteItemType[]
  },
  data: () => ({
    filters,
    loading: true,
  }),
  methods: {
    itemClasses(type: string): string {
      return isTextField(type) ? 'hidden' : 'number';
    },

    itemValueForType(quoteItemType: QuoteItemType, quoteItems?: QuoteItem[]) {
      if (!quoteItems) {
        return '';
      }

      const item = quoteItems.find(i => i.quoteItemTypeId === quoteItemType.quoteItemTypeId);

      if (!item || !item.value) {
        return '';
      }

      switch (quoteItemType.optionType) {
        case 'percent':
          return `${ addCommas(item.value) }%`;
        case 'currency':
          return `$${ addCommas(item.value) }`;
      }
      return item.value;
    }
  },
  computed: {
    numHiddenFields() {
      if (!this.quoteItemTypes) {
        return 0;
      }
      return this.quoteItemTypes.reduce((total, type) => isTextField(type.optionType) ? total + 1 : total, 0);
    },
    showRetention() {
      if (!this.layerType) {
        return true;
      }
      return ('' + this.layerType).toLowerCase() === 'primary';
    },
    showAttachment() {
      if (!this.layerType) {
        return true;
      }
      return ('' + this.layerType).toLowerCase() !== 'primary';
    },
    carriers() {
      if (!this.quotes) {
        return [];
      }

      return this.quotes.map((q, index) => ({
        ...q,
        key: index,
        name: q.insurerGroup,
        limit: q.aggregateLimit,
        retention: q.retention,
        attachment: q.attachmentPoint,
        ppm: q.premium && q.aggregateLimit ? (q.premium / q.aggregateLimit) * 1000000 as number | null : null,
        admitted: q.admitted ? 'Yes' : 'No',
        commPctDisplay: q.commissionPercent ? `${Math.round((q.commissionPercent * 100 + Number.EPSILON) * 100) / 100}%` : ''
      }));
    }
  }
};
</script>

<style scoped>
h2 {
  margin-bottom: 1rem;
}

.table-container {
  max-height: 50vh;
  overflow-y: auto;
}

tr.declined td {
  opacity: 0.5;
}

.hidden {
  display: none;
}

.disclaimer {
  color: var(--v-gray-lighten1);
}
</style>
